import { Tags } from '~/api/esign/tags.autogen';
import {
  pusherStore,
  registerPusherResync,
} from '~/common/contexts/pusherStore';
import { PusherEventType } from '~/enums/PusherEventType';
import { api } from '~/redux/api/createApi';
import { esignApiRoot } from '~/redux/api/esign/root';
import { TagType } from '~/redux/api/TagType';
import { logger } from '~/utils/logger';
import { captureMessage } from '~/utils/sentry';

export type SubscribeToEsignUpdatesArg = {
  ticketId: string;
  /** Tenant id namespaced into the channel (mirrors K2 / EKP-58738). */
  clientId: number;
};

type EsignPackageUpdatedEvent = {
  ticket_id?: string;
  invalidate_esign_package?: boolean;
};

type EsignCacheLifecycle = {
  cacheDataLoaded: Promise<{ data: null; meta: unknown }>;
  cacheEntryRemoved: Promise<void>;
  dispatch: (action: unknown) => void;
};

/**
 * RTK Query `onCacheEntryAdded` handler for real-time esign package updates.
 *
 * Subscribes to `private-esign-ticket-{clientId}-{ticketId}` when the cache
 * entry is created. On each `esign-package-updated` invalidate signal,
 * refetches the CLM esign package query and (for the legacy Everest ticket
 * page follow-up) the legacy envelope/recipients/activities tags. The channel
 * is unsubscribed when the cache entry is removed.
 *
 * Mount via the dummy `subscribeToEsignUpdates` endpoint at a single page/
 * stage root — do not bind this to `getPackage` or call it from
 * `useEsignVendor` (which has multiple consumers on the sign card).
 */
export const onEsignCacheEntryAdded = async (
  arg: SubscribeToEsignUpdatesArg,
  { cacheDataLoaded, cacheEntryRemoved, dispatch }: EsignCacheLifecycle,
) => {
  await cacheDataLoaded;

  const { ticketId, clientId } = arg;
  if (!ticketId) {
    return;
  }
  if (!Number.isFinite(clientId)) {
    logger.pusher(
      'skipped esign ticket %s subscription: no client id on currentUser',
      ticketId,
    );
    captureMessage('Pusher esign subscription skipped: unknown client id', {
      section: 'pusher subscription',
      resource_kind: 'esign',
    });
    return;
  }

  const channelName = `private-esign-ticket-${clientId}-${ticketId}`;
  const pusher = pusherStore.getPusher();
  const channel = pusherStore.subscribe(pusher, channelName, {
    success: () => {
      logger.pusher('subscribed %s', channelName);
    },
    error: (subscriptionError) => {
      logger.pusher(
        'subscription failed %s %o',
        channelName,
        subscriptionError,
      );
      captureMessage(`Pusher subscription failed for ${channelName}`, {
        section: 'pusher subscription',
        resource_kind: 'esign',
        subscription_status: String(subscriptionError?.status),
      });
    },
  });

  const invalidateEsignCaches = () => {
    const esignTags = [{ type: Tags.Packages, id: ticketId }];
    const legacyTags = [
      { type: TagType.EsignatureEnvelope, id: ticketId },
      { type: TagType.TicketRecipients, id: ticketId },
      { type: TagType.EsignatureActivities, id: ticketId },
    ];

    logger.pusher('%s → invalidating %o', channelName, [
      ...esignTags,
      ...legacyTags,
    ]);
    dispatch(esignApiRoot.util.invalidateTags(esignTags));
    dispatch(api.util.invalidateTags(legacyTags));
  };

  const onPackageUpdated = (event: EsignPackageUpdatedEvent) => {
    logger.pusher(
      '%s → %s %o',
      channelName,
      PusherEventType.EsignPackageUpdated,
      event,
    );

    if (event.invalidate_esign_package !== true) {
      return;
    }
    // Defense-in-depth: channel is ticket-scoped, but still ignore a payload
    // whose ticket_id doesn't match this subscription's ticket.
    if (event.ticket_id && event.ticket_id !== ticketId) {
      return;
    }

    invalidateEsignCaches();
  };

  channel.bind(PusherEventType.EsignPackageUpdated, onPackageUpdated);

  const unregisterResync = registerPusherResync(() => {
    logger.pusher('resyncing %s after reconnect', channelName);
    invalidateEsignCaches();
  });

  await cacheEntryRemoved;
  channel.unbind(PusherEventType.EsignPackageUpdated, onPackageUpdated);
  unregisterResync();
  logger.pusher('unsubscribed %s', channelName);
  pusher.unsubscribe(channelName);
};
